import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { COMING_SOON_PATHS } from './svg-paths';
import { getAudioService } from '@/shared/services/AudioService';

interface ComingSoonProps {
  /**
   * Whether the tooltip is visible
   */
  isOpen: boolean;

  /**
   * Callback when the tooltip should close
   */
  onClose: () => void;
}

/**
 * ComingSoon Tooltip Component
 *
 * A tiny, auto-dismissing tooltip that displays "Coming soon" message.
 * Features smart positioning (prefers bottom, falls back to top if no space),
 * built-in arrow that points to trigger button, auto-dismiss after 5 seconds,
 * and sound effect on appear.
 *
 * The tooltip shape includes an integrated arrow in the SVG design (left side).
 * When positioned above the button, only the SVG layers flip vertically (scaleY)
 * to point the arrow downward while preserving the 3D effect and keeping text upright.
 *
 * @example
 * ```tsx
 * const [showTooltip, setShowTooltip] = useState(false);
 *
 * <div className="relative">
 *   <button onClick={() => setShowTooltip(true)}>Trigger</button>
 *   <ComingSoon isOpen={showTooltip} onClose={() => setShowTooltip(false)} />
 * </div>
 * ```
 */
export default function ComingSoon({ isOpen, onClose }: ComingSoonProps) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<'bottom' | 'top'>('bottom');

  // Auto-dismiss after 5 seconds
  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      onClose();
    }, 5000);

    return () => {
      clearTimeout(timer);
    };
  }, [isOpen, onClose]);

  // Play sound effect when tooltip appears
  useEffect(() => {
    if (isOpen) {
      getAudioService().playSFX('pose');
    }
  }, [isOpen]);

  // Smart positioning: check available space and position accordingly
  useEffect(() => {
    if (isOpen && tooltipRef.current) {
      const tooltipElement = tooltipRef.current;
      const rect = tooltipElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Check if there's enough space below (need ~130px for tooltip)
      const spaceBelow = viewportHeight - rect.top;
      const spaceAbove = rect.top;

      // Prefer bottom, but use top if not enough space below
      if (spaceBelow < 130 && spaceAbove > 130) {
        setPosition('top');
      } else {
        setPosition('bottom');
      }
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={tooltipRef}
          initial={{ opacity: 0, y: position === 'bottom' ? -10 : 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: position === 'bottom' ? -10 : 10 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className={`absolute z-30 left-0 pointer-events-none ${
            position === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2'
          }`}
          style={{
            width: '180px', // Tiny size as requested
            height: '62px'
          }}
        >
          <div className="relative size-full">
            <svg
              className="absolute inset-0 block size-full"
              fill="none"
              preserveAspectRatio="none"
              viewBox="0 0 367 127"
              style={{
                transform: position === 'top' ? 'scaleY(-1)' : 'none',
                transformOrigin: 'center'
              }}
            >
              {/* Shadow layer */}
              <path d={COMING_SOON_PATHS.shadow} fill="#0350A0" />
              {/* Border layer */}
              <path d={COMING_SOON_PATHS.border} fill="black" />
              {/* Main gradient shape */}
              <path d={COMING_SOON_PATHS.main} fill="url(#paint0_linear_coming_soon)" />
              <defs>
                <linearGradient
                  gradientUnits="userSpaceOnUse"
                  id="paint0_linear_coming_soon"
                  x1="180"
                  x2="180"
                  y1="0"
                  y2="127"
                >
                  <stop stopColor="#1C71DC" />
                  <stop offset="1" stopColor="#1778F2" />
                </linearGradient>
              </defs>
            </svg>

            {/* Text - always upright, using Mind Boggle font */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span
                className="text-white font-semibold text-[21px] leading-none"
                style={{ fontFamily: 'Mind Boggle, sans-serif' }}
              >
                Coming soon
              </span>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
